import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Seo from '../Seo';
import Breadcrumbs, { buildBreadcrumbSchema } from '../Breadcrumbs';
import Navbar from '../Navbar';
import Footer from '../Footer';
import NotFound from '../NotFound';
import { playStoreUrl, trackEvent } from '../analytics';
import landingData from './landingData';
import FromTheBlog from '../blog/components/FromTheBlog';
import '../App.css';
import './LandingPage.css';

// Content-engine addition (see src/blog/components/FromTheBlog.js): the
// Landing Pages → Blog Articles link in the internal-linking chain. Renders
// nothing until a matching post is published, so it changes nothing about
// today's page — this is the one deliberate touch to this otherwise-frozen
// file, isolated to a single mapping + a single render call below.
const BLOG_CATEGORY_BY_SLUG = {
  'anonymous-chat-app': 'safety-trust',
  'voice-chat-app': 'culture',
  'audio-chat-app': 'culture',
  'talk-to-someone': 'wellness',
  'emotional-support': 'wellness',
  'mental-wellness': 'wellness',
  'relationship-advice': 'relationships',
  'stress-relief': 'wellness',
  'loneliness-help': 'wellness',
  'online-friends': 'relationships',
  'earn-money-talking-online': 'product-updates',
  'late-night-chat-app': 'wellness',
  'hindi-chat-app': 'culture',
};

function buildFaqSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

// One route (see App.js) renders every /<slug> landing page from here, keyed
// by landingData. Adding an 11th page is "add an object to landingData" —
// no new component, no new route wiring beyond one line.
function LandingPageInner({ data }) {
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  const breadcrumbItems = [
    { name: 'Home', path: '/' },
    { name: data.breadcrumbLabel, path: `/${data.slug}` },
  ];
  const jsonLd = [buildBreadcrumbSchema(breadcrumbItems), buildFaqSchema(data.faqs)];
  const ctaLoc = `landing_${data.slug}`;

  return (
    <>
      <Seo
        title={data.title}
        description={data.description}
        path={`/${data.slug}`}
        image={`https://baatcheetapp.in${data.heroImage.src}`}
        imageAlt={data.heroImage.alt}
        imageWidth={data.heroImage.w}
        imageHeight={data.heroImage.h}
        jsonLd={jsonLd}
      />
      <div className="bg" />
      <div className="confetti">
        <i className="dot s1" /><i className="cbar s2" /><i className="dot s3" /><i className="cbar s4" />
        <i className="dot s5" /><i className="dot s6" /><i className="cbar s7" /><i className="dot s8" />
        <i className="cbar s9" /><i className="dot s10" />
      </div>

      <Navbar />
      <Breadcrumbs items={breadcrumbItems} />

      <main>
        {/* ── HERO ── */}
        <section className="lp-hero shell">
          <div className="lp-hero-grid">
            <div className="lp-hero-copy reveal">
              <span className="kicker"><span className="kicker-line" /> {data.kicker}</span>
              <h1 className="lp-h1">{data.h1}</h1>
              <p className="lead">{data.heroLead}</p>
              <div className="hero-cta">
                <a
                  className="btn btn-cyan"
                  href={playStoreUrl(ctaLoc)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('play_store_click', { location: ctaLoc })}
                >
                  ▶ Get Baatcheet Free
                </a>
                <Link className="btn btn-ghost" to="/how-it-works">See how it works</Link>
              </div>
            </div>
            <div className="lp-hero-img-wrap reveal">
              <div className="lp-photo">
                <img
                  src={data.heroImage.src}
                  alt={data.heroImage.alt}
                  width={data.heroImage.w}
                  height={data.heroImage.h}
                  fetchpriority="high"
                />
                {data.heroImage.label && <div className="lp-photo-label">{data.heroImage.label}</div>}
              </div>
            </div>
          </div>
        </section>

        {/* ── CONTENT SECTIONS ── */}
        {data.sections.map((s, i) => (
          <section className="block lp-section shell" key={s.heading}>
            <div className="reveal">
              <h2 className="h">{s.heading}</h2>
              {s.paragraphs.map((p, pi) => <p className="lead lp-p" key={pi}>{p}</p>)}
              {s.bullets && (
                <ul className="lp-bullets">
                  {s.bullets.map((b, bi) => <li key={bi}>{b}</li>)}
                </ul>
              )}
            </div>
          </section>
        ))}

        {/* ── QUOTE ── */}
        {data.quote && (
          <section className="shell lp-quote-wrap reveal">
            <blockquote className="lp-quote">
              “{data.quote.text}”
              <footer>— {data.quote.attribution}</footer>
            </blockquote>
          </section>
        )}

        {/* ── DISCLAIMER (wellness-cluster pages only) ── */}
        {data.disclaimer && (
          <section className="shell reveal">
            <p className="lp-disclaimer">{data.disclaimer}</p>
          </section>
        )}

        {/* ── FAQ ── */}
        <section className="block lp-faq shell">
          <div className="reveal">
            <div className="kicker"><span className="kicker-line" /> FAQs</div>
            <h2 className="h">Frequently Asked <span className="stamp">Questions</span></h2>
          </div>
          <div className="lp-faq-list reveal">
            {data.faqs.map((f, i) => (
              <details className="lp-faq-item" key={i}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ── RELATED ── */}
        <div className="lp-related shell">
          <span>Related:</span>
          {data.related.map(r => (
            <Link key={r.path} to={r.path}>{r.label}</Link>
          ))}
        </div>

        <FromTheBlog category={BLOG_CATEGORY_BY_SLUG[data.slug]} />

        {/* ── CTA ── */}
        <section className="block" style={{ paddingTop: 0 }}>
          <div className="shell">
            <div className="cta reveal">
              <div className="cta-grid">
                <div className="cta-l">
                  <div className="kicker"><span className="kicker-line" /> Free to start</div>
                  <h2>{data.ctaHeading || <>Try it. <span className="stamp">Talk today.</span></>}</h2>
                  <p className="lead">{data.ctaBody || 'Download Baatcheet free on Android and have your first conversation in under a minute.'}</p>
                  <div className="app-row">
                    <a
                      className="app-btn"
                      href={playStoreUrl(`${ctaLoc}_footer`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent('play_store_click', { location: `${ctaLoc}_footer` })}
                    >
                      <svg width="22" height="24" viewBox="0 0 512 512" aria-hidden="true">
                        <path fill="#4285F4" d="M48,59.49v393a4.33,4.33,0,0,0,7.37,3.07L260,256,55.37,56.42A4.33,4.33,0,0,0,48,59.49Z" />
                        <path fill="#34A853" d="M345.8,174,89.17,32.64l-.07,0c-6.81-3.64-13.36.55-13.36,7.71L260,256Z" />
                        <path fill="#EA4335" d="M89.1,479.36c0,7.16,6.55,11.35,13.36,7.71L345.8,338,260,256Z" />
                        <path fill="#FBBC04" d="M447.34,230.13l-82.08-45.79L260,256l105.26,71.66,82.08-45.79a20.41,20.41,0,0,0,0-51.74Z" />
                      </svg>
                      <div><div className="top">GET IT ON</div><div className="bot">Google Play</div></div>
                    </a>
                  </div>
                </div>
                <div className="cta-r">
                  <div style={{ fontSize: '15px', color: 'rgba(255,255,255,.85)', fontFamily: "'DM Mono',monospace", letterSpacing: '.06em', textAlign: 'center' }}>
                    ★ 4.8 rated · 1.2M+ users<br />Made in Bharat
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default function LandingPage() {
  const { slug } = useParams();
  const data = landingData[slug];
  // Render NotFound in place rather than <Navigate to="/404">: "/404" is
  // itself a single path segment, so a redirect would just match this same
  // /:slug route again with slug="404" and loop forever.
  if (!data) return <NotFound />;
  return <LandingPageInner data={data} />;
}
